import { useState, useCallback, useMemo } from 'react';
import { makeAuthenticatedRequest } from '../utils/apiHelpers';

const API_URL = process.env.REACT_APP_API_URL;

const useProjectSelection = ({
  token,
  selectedProjectTypes,
  filteredGenerations,
  filteredBulkGenerations,
  allFilteredProjects,
  selectedBulkProject,
  setGenerations,
  setBulkGenerations,
  setSelectedBulkProject,
  setBulkExcelData,
}) => {
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedBulkProjects, setSelectedBulkProjects] = useState([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isBulkSelectionMode, setIsBulkSelectionMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Selection toggle helper
  const toggleSelection = (setter) => (id, isSelected) => {
    setter(prev => isSelected ? [...prev, id] : prev.filter(x => x !== id));
  };
  const handleProjectSelection = toggleSelection(setSelectedProjects);
  const handleBulkProjectSelection = toggleSelection(setSelectedBulkProjects);

  // Toggle select all helper
  const toggleSelectAll = (filteredList, selected, setter) => {
    const filteredIds = filteredList.map(item => item._id);
    const allSelected = selected.length === filteredIds.length && filteredIds.every(id => selected.includes(id));
    setter(allSelected ? [] : filteredIds);
  };

  const handleSelectAll = useCallback(() => {
    toggleSelectAll(filteredGenerations, selectedProjects, setSelectedProjects);
  }, [filteredGenerations, selectedProjects.length]);

  const handleSelectAllBulkProjects = useCallback(() => {
    toggleSelectAll(filteredBulkGenerations, selectedBulkProjects, setSelectedBulkProjects);
  }, [filteredBulkGenerations, selectedBulkProjects.length]);

  // Toggle selection mode
  const toggleMode = (setMode, clearSelected) => {
    setMode(prev => { if (prev) clearSelected([]); return !prev; });
  };
  const toggleSelectionMode = useCallback(() => toggleMode(setIsSelectionMode, setSelectedProjects), []);
  const toggleBulkSelectionMode = useCallback(() => toggleMode(setIsBulkSelectionMode, setSelectedBulkProjects), []);

  const handleCloseSelection = useCallback(() => {
    setIsSelectionMode(false);
    setIsBulkSelectionMode(false);
    setSelectedProjects([]);
    setSelectedBulkProjects([]);
  }, []);

  const handleEnableSelection = useCallback(() => {
    const hasBothTypes = selectedProjectTypes.includes('single') && selectedProjectTypes.includes('bulk');
    if (hasBothTypes) {
      setIsSelectionMode(true);
      setIsBulkSelectionMode(true);
    } else if (selectedProjectTypes.includes('single')) {
      toggleSelectionMode();
    } else {
      toggleBulkSelectionMode();
    }
  }, [selectedProjectTypes, toggleSelectionMode, toggleBulkSelectionMode]);

  const isAllSelected = useMemo(() => {
    const hasBothTypes = selectedProjectTypes.includes('single') && selectedProjectTypes.includes('bulk');
    const allSingleSelected = selectedProjects.length === filteredGenerations.length && filteredGenerations.length > 0;
    const allBulkSelected = selectedBulkProjects.length === filteredBulkGenerations.length && filteredBulkGenerations.length > 0;
    if (hasBothTypes) return allSingleSelected && allBulkSelected && allFilteredProjects.length > 0;
    if (isSelectionMode) return allSingleSelected;
    if (isBulkSelectionMode) return allBulkSelected;
    return false;
  }, [
    selectedProjectTypes, selectedProjects.length, selectedBulkProjects.length,
    filteredGenerations.length, filteredBulkGenerations.length, allFilteredProjects.length,
    isSelectionMode, isBulkSelectionMode
  ]);

  const handleSelectAllClick = useCallback(() => {
    const hasBothTypes = selectedProjectTypes.includes('single') && selectedProjectTypes.includes('bulk');
    if (hasBothTypes) {
      handleSelectAll();
      handleSelectAllBulkProjects();
    } else if (isSelectionMode) {
      handleSelectAll();
    } else if (isBulkSelectionMode) {
      handleSelectAllBulkProjects();
    }
  }, [selectedProjectTypes, isSelectionMode, isBulkSelectionMode, handleSelectAll, handleSelectAllBulkProjects]);

  const handleDeleteSelected = () => {
    if (selectedProjects.length + selectedBulkProjects.length === 0) return;
    setShowDeleteConfirm(true);
  };

  const handleConfirmedDelete = useCallback(async () => {
    if (selectedProjects.length + selectedBulkProjects.length === 0) return;
    setIsDeleting(true);
    try {
      if (selectedProjects.length > 0) {
        const response = await makeAuthenticatedRequest(
          `${API_URL}/single-gen/delete-multiple-generations`, token,
          { method: 'post', data: { generation_ids: selectedProjects } }
        );
        if (response.data.success) {
          setGenerations(prev => prev.filter(gen => !selectedProjects.includes(gen._id)));
          setSelectedProjects([]);
        }
      }
      if (selectedBulkProjects.length > 0) {
        const deletePromises = selectedBulkProjects.map(projectId =>
          makeAuthenticatedRequest(
            `${API_URL}/bulk-generation-v2/delete-generation/${projectId}`, token,
            { method: 'delete' }
          ).then(response => ({ projectId, success: response.data.success }))
           .catch(() => ({ projectId, success: false }))
        );
        const results = await Promise.all(deletePromises);
        const deletedIds = results.filter(r => r.success).map(r => r.projectId);
        setBulkGenerations(prev => prev.filter(project => !deletedIds.includes(project._id)));
        if (selectedBulkProject && deletedIds.includes(selectedBulkProject._id)) {
          setSelectedBulkProject(null);
          setBulkExcelData(null);
        }
        setSelectedBulkProjects([]);
      }
      setShowDeleteConfirm(false);
    } catch (error) {
    } finally {
      setIsDeleting(false);
    }
  }, [selectedProjects, selectedBulkProjects, selectedBulkProject, token,
      setGenerations, setBulkGenerations, setSelectedBulkProject, setBulkExcelData]);

  // Called on logout to clear all selection state
  const clearAll = useCallback(() => {
    setSelectedProjects([]);
    setSelectedBulkProjects([]);
    setIsSelectionMode(false);
    setIsBulkSelectionMode(false);
  }, []);

  return {
    selectedProjects, selectedBulkProjects,
    isSelectionMode, isBulkSelectionMode,
    isDeleting, showDeleteConfirm, setShowDeleteConfirm,
    isAllSelected,
    handleProjectSelection, handleBulkProjectSelection,
    handleSelectAllClick,
    handleEnableSelection, handleCloseSelection,
    handleDeleteSelected, handleConfirmedDelete,
    clearAll,
  };
};

export default useProjectSelection;
